// Packages and components
import React, { useContext } from 'react';
import { Box, Image } from 'grommet';
import YoutubeButton from '../Socials/Youtube/YoutubeButton';
import TrackGameCard from './TrackGameCard';
import SHDThemeOverride from '@ui/ThemeOverride/SHDThemeOverride';
import TeamInfo from './TeamInfo';
import TeamActionButtons from './TeamActionButtons';
import { useMediaQuery } from '@uidotdev/usehooks';
import { MediaQuery } from '@enums/MediaQueries';
import { TeamContext } from '@services/Session/context';
import FacebookButton from '@features/Socials/Facebook/FacebookButton';

interface TeamPageHeaderProps {
  isScoringLink: boolean;
  screenSize: string;
  setShowQrModal: (show: boolean) => void;
}

const TeamPageHeader = ({
  isScoringLink,

  screenSize,
  setShowQrModal,
}: TeamPageHeaderProps): React.ReactElement => {
  const headerId = 'team-page-header';
  const isLandscape = useMediaQuery(MediaQuery.IS_LANDSCAPE_MOBILE);
  const teamStore = useContext(TeamContext);
  const team = teamStore.fetchTeamInfo;
  const isAdmin = teamStore.isTeamManager;

  const handleMargin = () => {
    const margin = {
      right: '0px',
      top: '-60px',
    };

    if (screenSize === 'small') {
      margin.top = '-48px';
    }

    if (isLandscape) {
      margin.top = '0px';
      margin.right = '16px';
    }
    return margin;
  };

  return (
    <Box
      className={`${headerId}`}
      data-testid={`${headerId}`}
      direction="row"
      wrap={true}
    >
      <Box
        className={`${headerId}__top-row`}
        direction="row"
        justify="between"
        align={isLandscape ? 'center' : 'start'}
        pad={{ horizontal: screenSize === 'small' ? '0' : '16px' }}
        width="100%"
      >
        <Box
          className={`${headerId}__team-info`}
          direction={isLandscape ? 'row' : 'column'}
          align={isLandscape ? 'center' : 'start'}
        >
          <Box
            className={`${headerId}__team-info--avatar`}
            round="full"
            border={{
              side: 'all',
              color: 'white',
              size: '2px',
            }}
            height="80px"
            width="80px"
            overflow={'hidden'}
            flex={{ shrink: 0 }}
            background={'light-1'}
            margin={handleMargin()}
          >
            <Image
              fit="contain"
              src={team?.imageLogo?.small || 'image_default.jpg'}
            />
          </Box>
          <TeamInfo
            name={team?.nameLong}
            sport={team?.sport}
            ageLevel={team?.ageLevel}
          />
        </Box>

        <TeamActionButtons
          setShowQrModal={setShowQrModal}
          screenSize={screenSize}
          isScoringLink={isScoringLink}
          teamId={team.id}
          teamNameMed={team.nameMed}
        />
      </Box>

      <Box
        align={screenSize === 'large' ? 'center' : 'start'}
        className={`${headerId}__bottom-row`}
        direction={screenSize === 'large' ? 'row' : 'column'}
        gap={screenSize === 'large' ? '0' : '16px'}
        justify="between"
        margin={{ bottom: '16px' }}
        pad={{ horizontal: screenSize === 'small' ? '0' : '16px' }}
        width={screenSize === 'large' ? '100%' : '100%'}
      >
        <Box
          className={`${headerId}__team-metadata--stream-destination-buttons`}
          direction="row"
          gap="small"
          justify="start"
          key="stream-destination-buttons"
          margin={{ top: 'xxsmall' }}
          pad={{ top: '16px' }}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <YoutubeButton variant="team-page" truncateLength={25} />

          <FacebookButton variant="team-page" />
        </Box>

        {isAdmin && (
          <>
            <Box width={screenSize === 'small' ? '100%' : 'auto'}>
              <SHDThemeOverride full={false}>
                <TrackGameCard screenSize={screenSize} />
              </SHDThemeOverride>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TeamPageHeader;
